import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import config from './config';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Route-based Code Splitting: Lazy load pages to reduce initial bundle size
const Landing = lazy(() => import('./pages/Landing'));
const Auth = lazy(() => import('./pages/Auth'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CategoryBoard = lazy(() => import('./pages/CategoryBoard')); // Handles Movies, TV, NFL, etc.
const RankingEditor = lazy(() => import('./pages/RankingEditor'));
const Profile = lazy(() => import('./pages/Profile'));
const Privacy = lazy(() => import('./pages/Privacy'));
const UsernameSetup = lazy(() => import('./pages/UsernameSetup'));
const Terms = lazy(() => import('./pages/Terms'));
const About = lazy(() => import('./pages/About'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const Comparison = lazy(() => import('./pages/Comparison'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const PublicBoard = lazy(() => import('./pages/PublicBoard'));
const Legal = lazy(() => import('./pages/Legal'));

// Resource Pages
const HowItWorks = lazy(() => import('./pages/resources/HowItWorks'));
const Methodology = lazy(() => import('./pages/resources/Methodology'));
const WisdomOfCrowds = lazy(() => import('./pages/resources/WisdomOfCrowds'));
const DataIntegrity = lazy(() => import('./pages/resources/DataIntegrity'));
const RankingTips = lazy(() => import('./pages/resources/RankingTips'));
const CommunityGuidelines = lazy(() => import('./pages/resources/CommunityGuidelines'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <AuthCallback />
      </Suspense>
    );
  }
  
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/setup-username" element={<ProtectedRoute><UsernameSetup /></ProtectedRoute>} />
        <Route path="/compare/:rankingId" element={<ProtectedRoute><Comparison /></ProtectedRoute>} />
        
        {/* Legal & Info Pages */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/about" element={<About />} />
        <Route path="/legal" element={<Legal />} />
        
        {/* App Pages */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        
        {/* Dynamic Category Board (Movies, TV, NFL, etc.) */}
        <Route path="/category/:categoryId" element={<CategoryBoard />} />
        
        <Route path="/rank/:categoryId" element={<ProtectedRoute><RankingEditor /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly={true}><AdminDashboard /></ProtectedRoute>} />
        
        {/* Public Routes */}
        <Route path="/user/:username" element={<PublicProfile />} />
        <Route path="/boards/:slug" element={<PublicBoard />} />

        {/* Resources */}
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/wisdom-of-crowds" element={<WisdomOfCrowds />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        <Route path="/data-integrity" element={<DataIntegrity />} />
        <Route path="/ranking-tips" element={<RankingTips />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <AuthProvider>
          <Helmet>
            <title>{config.meta.defaultTitle}</title>
            <meta name="description" content={config.meta.defaultDescription} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={config.meta.siteUrl} />
            <meta property="og:site_name" content="Consensus Big Boards" />
          </Helmet>
          <BrowserRouter>
            <ScrollToTop />
            <div className="App min-h-screen noise-bg">
              <AppRouter />
              <Toaster position="top-center" richColors />
              <Footer />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
